import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Star, ThumbsUp } from '@phosphor-icons/react';
import { StarRating } from './StarRating';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getH = () => ({});

export function ProductReviews({ itemId, reviews, setReviews, reviewStats, setReviewStats }) {
    const [newRating, setNewRating] = useState(0);
    const [newReviewText, setNewReviewText] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);

    const submitReview = async () => {
        if (newRating === 0) { toast.error('Select a rating'); return; }
        setSubmittingReview(true);
        try {
            const res = await fetch(`${API}/reviews`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getH() },
                credentials: 'include',
                body: JSON.stringify({ item_id: itemId, rating: newRating, text: newReviewText }),
            });
            if (res.ok) {
                const review = await res.json();
                setReviews(prev => [review, ...prev]);
                setReviewStats(prev => ({
                    review_count: prev.review_count + 1,
                    avg_rating: ((prev.avg_rating * prev.review_count) + newRating) / (prev.review_count + 1),
                }));
                setNewRating(0); setNewReviewText(''); setShowReviewForm(false);
                toast.success('Review posted!');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Failed to post review');
            }
        } catch { toast.error('Network error'); }
        finally { setSubmittingReview(false); }
    };

    const markHelpful = async (reviewId) => {
        try {
            await fetch(`${API}/reviews/${reviewId}/helpful`, {
                method: 'POST', credentials: 'include', headers: getH(),
            });
            setReviews(prev => prev.map(r =>
                r.review_id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r
            ));
        } catch { /* ignore */ }
    };

    return (
        <div className="mt-10">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg text-foreground" data-testid="reviews-heading">
                    Reviews {reviewStats.review_count > 0 && `(${reviewStats.review_count})`}
                </h2>
                {!showReviewForm && (
                    <Button size="sm" variant="outline" onClick={() => setShowReviewForm(true)} data-testid="write-review-btn">
                        <Star size={14} className="mr-1" />Write a Review
                    </Button>
                )}
            </div>

            {showReviewForm && (
                <Card className="glass-card border-primary/20 mb-4" data-testid="review-form">
                    <CardContent className="p-4 space-y-3">
                        <div>
                            <p className="text-xs text-muted-foreground mb-1">Your Rating</p>
                            <StarRating rating={newRating} size={24} interactive onChange={setNewRating} />
                        </div>
                        <textarea
                            value={newReviewText}
                            onChange={e => setNewReviewText(e.target.value)}
                            placeholder="Tell other riders about this product..."
                            maxLength={1000}
                            className="w-full bg-transparent border border-border rounded-xl px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/40 min-h-[80px] resize-none"
                            data-testid="review-text"
                        />
                        <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => { setShowReviewForm(false); setNewRating(0); setNewReviewText(''); }}>Cancel</Button>
                            <Button size="sm" className="btn-primary" onClick={submitReview} disabled={submittingReview || newRating === 0} data-testid="submit-review-btn">
                                {submittingReview ? 'Posting...' : 'Post Review'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {reviews.length === 0 ? (
                <Card className="glass-card">
                    <CardContent className="p-6 text-center">
                        <Star size={36} className="text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No reviews yet. Be the first!</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {reviews.map(r => (
                        <Card key={r.review_id} className="glass-card" data-testid={`review-${r.review_id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Avatar className="h-8 w-8 border border-border mt-0.5">
                                        <AvatarImage src={r.user_picture} />
                                        <AvatarFallback className="text-[10px] bg-muted">{(r.user_name || '?')[0]}</AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-semibold text-foreground">{r.user_name}</p>
                                            <p className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <StarRating rating={r.rating} size={12} />
                                        {r.text && <p className="text-sm text-muted-foreground mt-1.5">{r.text}</p>}
                                        <button onClick={() => markHelpful(r.review_id)}
                                            className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground hover:text-primary transition-colors">
                                            <ThumbsUp size={12} />Helpful ({r.helpful_count || 0})
                                        </button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
